import { h, Component, Host, State, Element, Prop, Watch } from "@stencil/core";
import { ColorableComponent, applySchemeDelegate, setCssVariablesDelegate } from "../../common/colorable-component";
import { PromoFlagSchemeDefinition, PromoFlagSchemeName } from "./constants/promo-flag-types";
import { PROMO_FLAG_SCHEME_CONFIG, PROMO_FLAG_SCHEME_MAP } from "./constants/promo-flag-constants";
import { PromoFlagSchemeConfiguration } from "../../constants/colors";
import pfx from "../../utils/style-class-prefix-helper";
import { ICONSIZE_NORMAL, ICONSIZE_SMALL } from "../icon/constants/icon-constants";
import { InfoIconSchemeName } from "../info-icon/constants/info-icon-types";

/**
 * @slot [unnamed] - holds any number of HTML elements.
 */
@Component({
  tag: "eon-ui-promo-flag",
  styleUrl: "promo-flag.scss",
  shadow: true
})
export class PromoFlag implements ColorableComponent<PromoFlagSchemeConfiguration<PromoFlagSchemeDefinition>> {
  schemeConfig: PromoFlagSchemeConfiguration<PromoFlagSchemeDefinition> = PROMO_FLAG_SCHEME_CONFIG;
  schemeMap = PROMO_FLAG_SCHEME_MAP;

  /**
   * The components active scheme. If {@link scheme} is set, it will be set to the corresponding scheme.
   * If not set, the component will try to calculate it based on the nearest enclosing background information given by inner-background attribute or eon-ui-background component.
   */
  @State() activeScheme: PromoFlagSchemeDefinition;

  @Element() hostElement: HTMLEonUiPromoFlagElement;

  /**
   * The name of the color scheme of the component.
   * If not set, the component will try to calculate it based on the nearest enclosing background information given by inner-background attribute or eon-ui-background component.
   */
  @Prop({ reflect: true, mutable: true }) scheme: PromoFlagSchemeName;

  /**
   * Represents the internal color of the component.
   * It will be read by colorable-component.ts getColorOfNearestBackground() function to realize
   * the detection of backgrounds inside of components, hidden by shadow dom.
   * @disableControlInStorybook true
   */
  @Prop({ reflect: true, mutable: true }) innerBackground: string;

  /**
   * Promo flag alignment
   */
  @Prop() alignment: "left" | "center" | "right" = "center";

  /**
   * Defines the font size
   */
  @Prop() small: boolean = false;

  /**
   * Text for flag
   */
  @Prop() text: string;

  /**
   * Second Line Text for flag
   */
  @Prop() secondText: string;

  /**
   * Add info-icon
   */
  @Prop() hasIcon: boolean = false;

  /**
   * Modal-Id for Info-Icon option
   */
  @Prop() modal: string;

  componentWillLoad() {
    this.applyScheme();
  }

  componentDidLoad() {
    this.setCssVariables();
  }

  @Watch("scheme")
  applyScheme() {
    applySchemeDelegate(this);
  }

  @Watch("activeScheme")
  setCssVariables(): void {
    setCssVariablesDelegate(this);
    this.innerBackground = this.activeScheme.backgroundColor;
  }

  render() {
    return (
      <Host>
        <div
          class={pfx({
            "promo-flag": true,
            [`alignment-${this.alignment}`]: !!this.alignment,
            "promo-flag-small": this.small,
            "promo-flag-multiline": !!this.secondText,
            "has-icon": !!this.hasIcon
          })}
        >
          <div class={pfx("promo-flag-text")}>
            <span>{this.text}</span>
            {this.secondText && <span>{this.secondText}</span>}
          </div>
          {this.hasIcon && (
            <div class={pfx("promo-flag-icon")}>
              <eon-ui-info-icon
                size={this.small ? ICONSIZE_SMALL : ICONSIZE_NORMAL}
                scheme={this.activeScheme.textColor.split("eonui-")[1] as InfoIconSchemeName}
                open-modal-id={this.modal}
              ></eon-ui-info-icon>
            </div>
          )}
          <div class={pfx("promo-flag-shadow")}></div>
        </div>
      </Host>
    );
  }
}
